import { RhIcon, RhPopoverMenu, RhPopoverToggle } from "@rhythm-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useMemo } from "react";

import api from "@/services/api";
import { useGlobalJobStages } from "@/services/apiHooks/recruiter";

import { Button } from "@/components/ui/Button";

import { OpportunityContext } from "./constants";

type TSelfAppliedTriggerProps = {
  onClick?: () => void;
};

export const SelfAppliedTrigger = (props: TSelfAppliedTriggerProps) => {
  return (
    <RhPopoverToggle asChild onClick={props.onClick}>
      <Button className="flex w-[260px]" variant="tertiary">
        <span className="flex w-full items-center justify-between truncate">
          Self Applied
        </span>
        <RhIcon icon="tabler:chevron-down" />
      </Button>
    </RhPopoverToggle>
  );
};

type TSelfAppliedOptionsProps = {
  onOptionClick: () => void;
};

export const SelfAppliedOptions = ({
  onOptionClick,
}: TSelfAppliedOptionsProps) => {
  const opportunity = useContext(OpportunityContext);
  const queryClient = useQueryClient();

  const { data: globalJobStages } = useGlobalJobStages();

  const underscreeningStatus = useMemo(
    () => globalJobStages?.find((item) => item.name === "Underscreening"),
    [globalJobStages],
  );

  const patchBulkCandidateStageStatusToUnderscreening = useMutation({
    mutationFn: (payload: { job_id: number }) => {
      if (!opportunity?.candidate_id) {
        return Promise.reject(new Error("Candidate ID is missing"));
      }
      if (typeof underscreeningStatus?.id === "undefined") {
        return Promise.reject(new Error("review stage id not found"));
      }
      return api.recruiter.patchBulkCandidateStageStatus({
        candidate_ids: [opportunity.candidate_id],
        stage: underscreeningStatus.id,
        job_id: payload.job_id,
      });
    },
    onSuccess(_, { job_id }) {
      queryClient.invalidateQueries({ queryKey: ["CANDIDATES", "LIST"] });
      queryClient.invalidateQueries({ queryKey: ["CANDIDATE_ACTIVITIES"] });
      queryClient.invalidateQueries({ queryKey: ["ACTIVITY_LOG_ACTORS"] });
      queryClient.invalidateQueries({ queryKey: ["JOB_REQUIREMENT_STATUS"] });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: job_id }],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "CANDIDATES_JOB_STAGES",
          "ITEM",
          { candidateId: opportunity?.candidate_id },
        ],
      });
    },
  });

  const moveToUnderscreening = () => {
    if (!opportunity?.job_id) return;
    patchBulkCandidateStageStatusToUnderscreening.mutate({
      job_id: opportunity.job_id,
    });
    onOptionClick();
  };

  return (
    <RhPopoverMenu
      className="relative z-50"
      disabled={patchBulkCandidateStageStatusToUnderscreening.isLoading}
    >
      <div
        className="max-h-[60vh] w-[260px] overflow-y-auto rounded bg-white pt-tnano shadow-[0px_4px_8px_0px_rgba(0,0,0,0.24)]"
        data-testid="stages-dialog-box"
      >
        <div
          onClick={moveToUnderscreening}
          className="flex cursor-pointer items-center gap-tpico p-2 hover:bg-gray-100"
        >
          <p>Move to underscreening</p>
        </div>
      </div>
    </RhPopoverMenu>
  );
};
